/**
 * Indexable, organic service pages for searches that had no page owning the term: jawline filler, tear trough
 * filler, nasolabial folds, hand filler, turkey neck. All copy, prices and FAQ text live in
 * shared/serviceGapPages.ts, which also feeds the FAQPage JSON-LD (via shared/additiveSeoFaqSchema.ts), so the
 * visible questions and the structured data are the same words. The /lp/ ad pages are untouched and stay noindex.
 */
import { Link } from "wouter";
import { useEffect } from "react";
import { trackViewContent } from "@/lib/gtm";
import TrademarkNote from "@/components/TrademarkNote";
import { SERVICE_GAP_PAGES, type ServiceGapPage } from "@shared/serviceGapPages";
import { ArticleSection, C, FaqList, PageHero, RelatedLinks, ServicePageShell } from "./AdditiveSeoPages";

function PriceTable({ page }: { page: ServiceGapPage }) {
  return (
    <ArticleSection title={page.priceHeading}>
      <div style={{ overflowX: "auto" }}>
        <table style={{ borderCollapse: "collapse", minWidth: 560, width: "100%", fontSize: ".95rem" }}>
          <thead>
            <tr style={{ background: C.warm, textAlign: "left" }}>
              {["Treatment", "Price", "Notes"].map((h) => (
                <th key={h} scope="col" style={{ border: `1px solid ${C.border}`, color: C.dark, padding: ".7rem .8rem" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {page.priceRows.map((r) => (
              <tr key={r.label} style={{ background: C.white, verticalAlign: "top" }}>
                <th scope="row" style={{ border: `1px solid ${C.border}`, color: C.dark, padding: ".7rem .8rem", textAlign: "left" }}>{r.label}</th>
                <td style={{ border: `1px solid ${C.border}`, color: C.dark, fontWeight: 700, padding: ".7rem .8rem", whiteSpace: "nowrap" }}>{r.price}</td>
                <td style={{ border: `1px solid ${C.border}`, color: C.muted, padding: ".7rem .8rem" }}>{r.note}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p style={{ fontSize: ".85rem", marginTop: ".9rem" }}>{page.priceFootnote}</p>
    </ArticleSection>
  );
}

function ServiceGapPageView({ route }: { route: string }) {
  const page = SERVICE_GAP_PAGES.find((p) => p.route === route)!;
  useEffect(() => { trackViewContent(`service-gap${route}`); }, [route]);
  const text = `${page.intro} ${page.sections.map((s) => s.paragraphs.join(" ")).join(" ")}`;
  const brands = ["Sculptra"].filter((b) => text.includes(b));
  return (
    <ServicePageShell stickyLabel={page.stickyLabel} stickyPrice={page.stickyPrice}>
      <PageHero eyebrow={page.eyebrow} title={page.h1} intro={page.intro} priceSummary={page.priceSummary} />
      <div className="container" style={{ maxWidth: 980 }}>
        <PriceTable page={page} />
        {page.sections.map((s) => (
          <ArticleSection key={s.title} title={s.title}>
            {s.paragraphs.map((p) => <p key={p.slice(0, 40)}>{p}</p>)}
          </ArticleSection>
        ))}
        <section aria-label={`${page.faqHeading}`} style={{ background: C.warm, border: `1px solid ${C.border}`, borderRadius: 18, margin: "1rem 0 3.5rem", padding: "clamp(1.3rem,4vw,2.4rem)" }}>
          <p style={{ color: C.accent, fontFamily: "'Lato',sans-serif", fontSize: ".74rem", fontWeight: 800, letterSpacing: ".15em", margin: "0 0 .45rem", textTransform: "uppercase" }}>Frequently asked questions</p>
          <h2 style={{ color: C.dark, fontFamily: "'Playfair Display',serif", fontSize: "clamp(1.5rem,3vw,2.1rem)", margin: "0 0 1rem" }}>{page.faqHeading}</h2>
          <FaqList faqs={page.faqs.map(({ q, a }) => ({ question: q, answer: a }))} />
        </section>
        <p style={{ color: C.muted, fontSize: ".85rem", margin: "0 0 2rem" }}>
          Results vary and no treatment is guaranteed to give a particular outcome. See also <Link href="/dermal-fillers" style={{ color: C.accent }}>all dermal filler options</Link>.
        </p>
        {brands.length > 0 && <div style={{ margin: "0 0 2.5rem" }}><TrademarkNote brands={brands} /></div>}
      </div>
      <RelatedLinks links={page.related} />
    </ServicePageShell>
  );
}

export const JawlineFillerDallas = () => <ServiceGapPageView route="/jawline-filler-dallas" />;
export const TearTroughFillerDallas = () => <ServiceGapPageView route="/tear-trough-filler-dallas" />;
export const NasolabialFoldsDallas = () => <ServiceGapPageView route="/nasolabial-folds-filler-dallas" />;
export const HandFillerDallas = () => <ServiceGapPageView route="/hand-filler-dallas" />;
export const TurkeyNeckDallas = () => <ServiceGapPageView route="/turkey-neck-treatment-dallas" />;
